import { GeckoComponentDataset, GeckoMeasurementUnit } from 'types'

/**
 * Libra-local port of `pages/details/extensions/fde/ffsLtaGeneration/helpers.ts`.
 * Pure helpers; no Rolodex / React deps. Used by the binning step to detect
 * whether the user-defined CDS / data-frame settings exist after Conduit
 * re-aggregation, gating the "Generate Local Thin Areas" button.
 */

export const USER_DEFINED_CSCAN_KEY = 'cscans-selected-measure-user-defined'

export function getInspectionLengthUnit(
  inspectionUnits: string | undefined
): GeckoMeasurementUnit | undefined {
  const unitId = inspectionUnits?.split('-')[0]
  return unitId ? GeckoMeasurementUnit.GetByID(unitId) : undefined
}

export function getCdsDataFrameSettings(
  cds: GeckoComponentDataset | undefined
) {
  if (!cds) {
    return undefined
  }
  return cds.data.metadata_flags.use_conduit
    ? cds.data.data_frame_settings
    : cds.data.ds_data_frame_settings
}

export function getUserDefinedDataFrameSettings(
  cds: GeckoComponentDataset | undefined
) {
  return getCdsDataFrameSettings(cds)?.[USER_DEFINED_CSCAN_KEY]
}

/**
 * Pretty-prints a `data_frame_settings` key for the bin-size dropdown.
 * "cscans-selected-measure-foo-bar" -> "Foo Bar". Empty suffix -> "Default".
 */
export function formatDataFrameSettingsLabel(key: string): string {
  const trimmed = key.replace(/^cscans-selected-measure-?/, '')
  if (trimmed.length === 0) {
    return 'Default'
  }
  return trimmed
    .split(/[-_]/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

/**
 * Builds `{ value, label }[]` for the bin-size `<Select>`. Ports
 * `buildDataFrameSettingsOptions` from
 * `pages/details/extensions/fde/ffsLtaGeneration/calculations.ts:186-205`.
 */
export function buildDataFrameSettingsOptions(
  allDataFrameSettings:
    | Record<
        string,
        {
          x_axis: { bin_size: number; measurement_unit: GeckoMeasurementUnit }
          y_axis: { bin_size: number; measurement_unit: GeckoMeasurementUnit }
        }
      >
    | undefined
): Array<{ value: string; label: string }> {
  if (!allDataFrameSettings) {
    return []
  }
  return Object.entries(allDataFrameSettings).map(([key, settings]) => ({
    value: key,
    label: `${formatDataFrameSettingsLabel(key)} (${settings.x_axis.bin_size} ${settings.x_axis.measurement_unit.id} * ${settings.y_axis.bin_size} ${settings.y_axis.measurement_unit.id})`,
  }))
}
